import { useState } from 'react';
import { Check, X, Pencil, Loader2, Inbox, Mail, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CATEGORY_LABEL } from '@/modules/sales-research/lib';
import type { CompanyCategory } from '@/modules/sales-research/types';
import { useOutreachQueue, useCrmAction, useMailSenders } from './api';
import { Textarea, TextInput, relTime } from './widgets';
import type { OutreachDraft } from './types';

export function ApprovalQueue() {
  const queueQ = useOutreachQueue('pending');
  const act = useCrmAction();
  const senders = useMailSenders().data ?? [];
  const [sender, setSender] = useState('');
  const eff = sender || senders.find((s) => s.is_default)?.address || senders[0]?.address || '';
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const drafts = queueQ.data ?? [];

  const allSelected = drafts.length > 0 && selected.size === drafts.length;
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(drafts.map((d) => d.id)));
  }
  function toggle(id: string) {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function bulk(action: 'approve_draft' | 'reject_draft') {
    const ids = [...selected];
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      await act.mutateAsync({ action, draft_id: id, ...(action === 'reject_draft' ? { reason: 'bulk reject' } : {}) });
      // Approving also parks the email in the chosen mailbox's Gmail Drafts.
      // eslint-disable-next-line no-await-in-loop
      if (action === 'approve_draft') await act.mutateAsync({ action: 'send_draft', draft_id: id, from_sender: eff });
    }
    setSelected(new Set());
  }

  if (queueQ.isLoading) {
    return <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 w-full" />)}</div>;
  }
  if (drafts.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-16 text-center text-sm text-muted-foreground">
        <Inbox className="h-7 w-7" />
        <p>The approval queue is empty.</p>
        <p className="text-xs">Run the draft agent to generate outreach for review. Nothing is ever sent without your approval.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} />
          Select all ({drafts.length})
        </label>
        {selected.size > 0 && (
          <>
            <span className="text-xs text-muted-foreground">{selected.size} selected</span>
            <Button size="sm" variant="outline" disabled={act.isPending} onClick={() => bulk('approve_draft')}>
              <Check className="h-3.5 w-3.5" /> Approve
            </Button>
            <Button size="sm" variant="outline" disabled={act.isPending} onClick={() => bulk('reject_draft')}>
              <X className="h-3.5 w-3.5" /> Reject
            </Button>
          </>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          Nothing sends automatically — approving saves the email to Gmail Drafts to review and send.
        </span>
      </div>

      {senders.length > 0 && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Draft from mailbox:</span>
          <select
            value={eff}
            onChange={(e) => setSender(e.target.value)}
            className="rounded-md border border-border/60 bg-background px-2 py-1"
          >
            {senders.map((s) => (
              <option key={s.address} value={s.address}>
                {s.label ? `${s.label} — ${s.address}` : s.address}{s.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {drafts.map((d) => (
        <DraftCard key={d.id} draft={d} selected={selected.has(d.id)} onSelect={() => toggle(d.id)} sender={eff} />
      ))}
    </div>
  );
}

function DraftCard({ draft, selected, onSelect, sender }: { draft: OutreachDraft; selected: boolean; onSelect: () => void; sender: string }) {
  const act = useCrmAction();
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(draft.subject ?? '');
  const [body, setBody] = useState(draft.body ?? '');
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const p = (draft.personalization ?? {}) as Record<string, any>;
  const noRecipient = !draft.to_email;

  async function save() {
    await act.mutateAsync({ action: 'edit_draft', draft_id: draft.id, subject, body });
    setEditing(false);
  }
  async function approve() {
    await act.mutateAsync({ action: 'approve_draft', draft_id: draft.id, subject, body });
    // Park the approved email in the chosen mailbox's Gmail Drafts (no-op if Gmail off).
    await act.mutateAsync({ action: 'send_draft', draft_id: draft.id, from_sender: sender });
  }
  async function reject() {
    await act.mutateAsync({ action: 'reject_draft', draft_id: draft.id, reason });
    setRejecting(false);
  }

  return (
    <Card className={selected ? 'ring-2 ring-[#51BFAA]' : ''}>
      <div className="space-y-3 p-4">
        <div className="flex items-start gap-3">
          <input type="checkbox" className="mt-1" checked={selected} onChange={onSelect} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{draft.company_name}</span>
              <Badge variant="outline" className="font-normal">{CATEGORY_LABEL[draft.company_category as CompanyCategory]}</Badge>
              {draft.sequence_step === 1 && <Badge variant="muted">follow-up</Badge>}
              {draft.generated_by === 'agent'
                ? <Badge variant="muted">✨ {draft.model}</Badge>
                : <Badge variant="muted">template</Badge>}
              <span className="text-2xs text-muted-foreground">{relTime(draft.created_at)}</span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
              <Mail className="h-3 w-3" />
              {draft.to_name && <span>{draft.to_name}</span>}
              {noRecipient
                ? <span className="inline-flex items-center gap-1 text-[#B15599]"><AlertTriangle className="h-3 w-3" />no recipient — add a contact email before sending</span>
                : <span>{draft.to_email}</span>}
              {draft.outlet_name && <span>· via Flax {draft.outlet_name}</span>}
              {p.distance_m != null && <span>· {p.distance_m < 1000 ? `${Math.round(p.distance_m)} m` : `${(p.distance_m / 1000).toFixed(1)} km`}</span>}
              {p.warm_orders > 0 && <span className="text-[#51BFAA]">· {p.warm_orders} warm orders</span>}
            </div>
          </div>
        </div>

        {draft.rationale && (
          <p className="rounded-md bg-muted/50 px-2.5 py-1.5 text-xs text-muted-foreground">{draft.rationale}</p>
        )}

        <div className="space-y-2">
          {editing ? (
            <>
              <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="font-sans text-[13px] leading-relaxed" />
            </>
          ) : (
            <div className="rounded-md border border-border/60 bg-card/40 p-3">
              <p className="mb-1 text-[13px] font-semibold">{subject || <span className="text-muted-foreground">(no subject)</span>}</p>
              <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground/90">{body}</p>
            </div>
          )}
        </div>

        {rejecting ? (
          <div className="flex items-center gap-2">
            <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" />
            <Button size="sm" variant="destructive" disabled={act.isPending} onClick={reject}>Confirm reject</Button>
            <Button size="sm" variant="ghost" onClick={() => setRejecting(false)}>Cancel</Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {editing ? (
              <>
                <Button size="sm" variant="outline" disabled={act.isPending} onClick={save}>Save edits</Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setSubject(draft.subject ?? ''); setBody(draft.body); }}>Cancel</Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setRejecting(true)}><X className="h-3.5 w-3.5" /> Reject</Button>
              <Button size="sm" disabled={act.isPending} onClick={approve}>
                {act.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Approve & save to Gmail
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
